import { redirect } from '@sveltejs/kit';
import { AUTH, currentUser } from '$lib/domain/auth';
import type { LayoutLoad } from './$types';

/**
 * The static contract for the whole app.
 *
 * `ssr = false` states the truth about this deployment: there is no Node server. The
 * build runs on a CI runner with no database, no Valkey and no session, and every page
 * here is behind a login and shows live rows from SQLite — so there is nothing
 * meaningful to render at build time. It also means `load` functions only ever run in
 * the browser, which is what lets them call the API directly with no `browser` guards.
 *
 * `prerender = true` still pays for itself. Each route becomes its own HTML file with
 * that route's module preloads already in the markup, so the browser fetches the right
 * chunks immediately instead of booting a generic shell and resolving the route first.
 *
 * Routes that cannot be prerendered — /reports/[id], whose ids are database rows — set
 * `export const prerender = false` and are served the `200.html` fallback configured in
 * svelte.config.js.
 */
export const ssr = false;
export const prerender = true;

export const load: LayoutLoad = async ({ fetch, url, depends }) => {
	depends(AUTH);

	const user = await currentUser(fetch);

	// A convenience, not a gate: every endpoint enforces access itself, so a tampered
	// client gets 401s rather than data. This just avoids rendering a shell full of
	// failed requests to someone who is signed out.
	if (!user && url.pathname !== '/login') {
		const returnTo = `${url.pathname}${url.search}`;
		redirect(307, `/login?return_to=${encodeURIComponent(returnTo)}`);
	}

	// Signing in again when already signed in is a dead end; send them onward.
	if (user && url.pathname === '/login') {
		redirect(307, url.searchParams.get('return_to') ?? '/');
	}

	return { user };
};
